import { useEffect, useState } from 'react';
import { usePipelineSnapshot } from '../hooks/usePipelineSnapshot';
import type { SubBatch } from '../hooks/usePipelineSnapshot';
import { WaveStripBar } from '../components/pipeline-monitor/WaveStrip';
import { SitesFeedStrip } from '../components/pipeline-monitor/SitesFeedStrip';
import { PipelineStageView } from '../components/pipeline-monitor/PipelineStageView';
import { SystemHealthSidebar } from '../components/pipeline-monitor/SystemHealthSidebar';
import { PipelineRow } from '../components/pipeline-monitor/PipelineRow';
import type { PipelineSiteRow, StageData, ItemStatus } from '../components/pipeline-monitor/PipelineRow';

// Pipeline Monitoring Dashboard
// Built post-PR-#950 outage (16h zero-AEO with no alert) per:
//   - docs/specs/2026-05-08-pipeline-monitoring-dashboard.md
//   - pagemind-vault/cross-cutting/decisions/2026-05-08-pipeline-dashboard-implementation-plan.md
//   - codex review (ship-with-6-changes)
//   - Lovable mockup github.com/jportell/pixel-perfect-83 (PipelineRow component ported)
//
// v1 scope: read-only, polling-only (5s), responsive split — desktop swim lanes
// + mobile per-site dot-rows. v2 adds Socket.io invalidation push + remaining 4 alerts
// + 3 codex signals (provider/model success rate, retry pile-up, AEO submitted-but-empty).

function useIsMobile(breakpoint = 1200) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < breakpoint;
  });
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// Build a Lovable-shaped per-site row from sub-batch data for the mobile layout.
function buildSiteRows(subBatches: SubBatch[]): PipelineSiteRow[] {
  // Group sub-batches by site domain; aggregate dots-as-products by stage.
  const bySite: Record<string, SubBatch[]> = {};
  for (const sb of subBatches) {
    if (!bySite[sb.siteDomain]) bySite[sb.siteDomain] = [];
    bySite[sb.siteDomain].push(sb);
  }
  return Object.entries(bySite).map(([site, list]) => {
    const totalCount = list.reduce((sum, s) => sum + s.productCount, 0);
    const stages: StageData[] = [
      makeStage('Enrich', 'process', list, 'enrichment'),
      makeStage('Score', 'process', list, 'benefit_scoring'),
      makeStage('Embed', 'process', list, 'embedding'),
      makeStage('Cohort', 'wait', list, 'waiting_for_cohort'),
      makeStage('AEO', 'hold', list, 'aeo_generation'),
      makeStage('Validate', 'hold', list, 'aeo_validation'),
    ];
    return { site, mode: list[0]?.triggerType || 'unknown', count: totalCount, stages };
  });
}

function makeStage(name: string, type: StageData['type'], list: SubBatch[], phase: string): StageData {
  // Compute aggregate progress + items across sub-batches for this phase.
  const inPhase = list.filter(s => s.currentPhase === phase || laterPhase(s.currentPhase, phase));
  const totalCount = inPhase.reduce((sum, s) => sum + s.productCount, 0);
  const succeeded = inPhase.reduce((sum, s) => sum + (succeededFor(s, phase) || 0), 0);
  const failed = inPhase.reduce((sum, s) => sum + (failedFor(s, phase) || 0), 0);
  const progress = totalCount > 0 ? Math.min(100, Math.round((succeeded / totalCount) * 100)) : 0;
  // Build items array: cap at 24 dots, distribute success/error/progress by ratio.
  const target = Math.min(24, Math.max(6, totalCount));
  const successDots = Math.round((succeeded / Math.max(totalCount, 1)) * target);
  const errorDots = Math.round((failed / Math.max(totalCount, 1)) * target);
  const items: ItemStatus[] = [];
  for (let i = 0; i < successDots; i++) items.push('success');
  for (let i = 0; i < errorDots; i++) items.push('error');
  while (items.length < target) items.push('progress');
  return { name, type, progress, items };
}

const PHASE_ORDER = ['enrichment', 'benefit_scoring', 'embedding', 'waiting_for_cohort', 'aeo_generation', 'aeo_validation'];

function laterPhase(current: string, target: string): boolean {
  return PHASE_ORDER.indexOf(current) > PHASE_ORDER.indexOf(target);
}

function succeededFor(sb: SubBatch, phase: string): number {
  switch (phase) {
    case 'enrichment': return sb.enrichment.succeeded || 0;
    case 'benefit_scoring': return sb.benefitScoring.succeeded || 0;
    case 'embedding': return sb.embedding.succeeded || 0;
    case 'aeo_generation': return sb.aeoGeneration.generated || 0;
    case 'aeo_validation': return sb.aeoValidation.validated || 0;
    default: return 0;
  }
}

function failedFor(sb: SubBatch, phase: string): number {
  switch (phase) {
    case 'enrichment': return sb.enrichment.failed || 0;
    case 'benefit_scoring': return sb.benefitScoring.failed || 0;
    case 'embedding': return sb.embedding.failed || 0;
    case 'aeo_generation': return sb.aeoGeneration.failed || 0;
    default: return 0;
  }
}

export default function PipelineMonitor() {
  const { snapshot, prevSnapshot, error, loading } = usePipelineSnapshot(5000);
  const isMobile = useIsMobile(1200);

  if (loading && !snapshot) {
    return (
      <div className="p-8 text-sm text-gray-500">Loading pipeline snapshot…</div>
    );
  }
  if (error && !snapshot) {
    return (
      <div className="p-8">
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
          Failed to load snapshot: {error}
        </div>
      </div>
    );
  }
  if (!snapshot) return null;

  const criticalAlerts = snapshot.alerts.filter(a => a.severity === 'critical');
  const allActive = [...snapshot.subBatches, ...snapshot.realtimeBatches];
  const siteRows = isMobile ? buildSiteRows(allActive) : [];

  return (
    <div className="px-4 py-6 lg:px-8 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pipeline Monitor</h1>
          <p className="text-xs text-gray-500 font-mono">
            Polling every {snapshot.cacheTtlMs / 1000}s · Last cache {new Date(snapshot.cachedAt).toLocaleTimeString()}
          </p>
        </div>
        {criticalAlerts.length > 0 && (
          <div className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
            {criticalAlerts.length} critical alert{criticalAlerts.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Critical alerts banner */}
      {criticalAlerts.length > 0 && (
        <div className="rounded-xl border-2 border-red-400 bg-red-50 p-3 space-y-1">
          {criticalAlerts.map(a => (
            <div key={a.name} className="text-sm text-red-700">
              <span className="font-semibold uppercase tracking-wider mr-2">{a.name}</span>
              {a.message}
            </div>
          ))}
        </div>
      )}

      {/* Sites strip */}
      <SitesFeedStrip sites={snapshot.sites} />

      {/* Wave / flow strip */}
      <WaveStripBar strip={snapshot.waveStrip} />

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          {isMobile ? (
            <div className="space-y-3">
              {siteRows.length === 0 ? (
                <div className="rounded-xl border bg-white shadow-sm p-6 text-center text-sm text-gray-500">
                  Pipeline is idle.
                </div>
              ) : (
                siteRows.map(row => <PipelineRow key={row.site} row={row} />)
              )}
            </div>
          ) : (
            <PipelineStageView
              batch={snapshot.subBatches}
              realtime={snapshot.realtimeBatches}
              prevBatch={prevSnapshot?.subBatches}
              prevRealtime={prevSnapshot?.realtimeBatches}
            />
          )}
        </div>
        <SystemHealthSidebar snapshot={snapshot} />
      </div>
    </div>
  );
}
